<template>
  <modal-wrap
    :id="id"
    ref="integrationsWoocommerceKeys"
    :name="modalName"
    :header-class="'integrations-woocommerce-keys-header'"
    :hide-footer="true"
    body-class="integrations-woocommerce-keys-modal"
    size="lg"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">
          {{ modalTitle }}
        </h4>
      </div>
    </template>
    <template #default>
      <b-form-group>
        <div class="form-group">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label><b>Credentials</b></label>
                <div>
                  Secrets could be found in WooCommerce admin panel:<br />
                  WooCommerce &rarr; Settings &rarr; Advanced &rarr; REST API
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label><b>Webhook URL (optional)</b></label>
                <div>
                  {{ webhooksUrl }}
                  <CopyToClipboard :text="webhooksUrl">
                    <button class="fa-copy fa" title="Copy" />
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Site Url</label>
                <VInput
                  v-model="url"
                  type="text"
                  name="url"
                  :rules="{ required: true, min: 10 }"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Key</label>
                <VInput
                  v-model="key"
                  type="text"
                  name="key"
                  :rules="{ required: true, min: 36 }"
                  placeholder="ck_..."
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Secret</label>
                <VInput
                  v-model="secret"
                  type="text"
                  name="secret"
                  :rules="{ required: true, min: 36 }"
                  placeholder="cs_..."
                />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Parameters</h4>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Import Type</label>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <input id="magento_scheduled" v-model="importType" type="radio" value="scheduled" />
                    <label for="magento_scheduled" class="radio-group">
                      <span class="radio-title">Scheduled</span>
                    </label>
                  </div>
                  <div class="col-12 col-md-6">
                    <input id="magento_on_demand" v-model="importType" type="radio" value="on_demand" />
                    <label for="magento_on_demand" class="radio-group">
                      <span class="radio-title">On Demand</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Interval (minutes)</label>
                <VInput
                  v-model="importInterval"
                  type="text"
                  name="import_interval"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="wc-trigger-order-status">Trigger Order Status</label>
                <select id="wc-trigger-order-status" v-model="triggerOrderStatus" class="form-control">
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="on-hold">On Hold</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Weight (g)</label>
                <VInput
                  v-model="defaultWeight"
                  type="text"
                  name="defaultWeight"
                  placeholder="Default Weight"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Height (cm)</label>
                <VInput
                  v-model="defaultHeight"
                  type="text"
                  name="defaultHeight"
                  placeholder="Default Height"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Width (cm)</label>
                <VInput
                  v-model="defaultWidth"
                  type="text"
                  name="defaultWidth"
                  placeholder="Default Width"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Length (cm)</label>
                <VInput
                  v-model="defaultLength"
                  type="text"
                  name="defaultLength"
                  placeholder="Default Length"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <b-form-checkbox v-model="active" :value="active"> Active </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <button type="button" class="btn create-btn btn-green" :disabled="isButtonDisabled" @click="connect">
            Connect
          </button>
        </div>
      </b-form-group>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { WooCommerceIntegrationModal } from '@/types/Models/Integration'
import { notify } from '@kyvg/vue3-notification'
import { BFormCheckbox, BFormGroup } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, watch, type ComputedRef, type Ref, toRaw } from 'vue'
import { WOOCOMMERCE } from '@/const/IntegrationTypes'
import { WOO_COMMERCE_KEYS } from '@/const/ModalTypes'
import { numbersOnly } from '@/services/Helpers'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import CopyToClipboard from '@/views/Components/Elements/CopyToClipboard.vue'

export default defineComponent({
  name: 'IntegrationsWoocommerceKeysModal',
  components: {
    BFormCheckbox,
    BFormGroup,
    ModalWrap,
    VInput,
    CopyToClipboard,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()
    const url: Ref<string> = ref('')
    const key: Ref<string> = ref('')
    const secret: Ref<string> = ref('')
    const importInterval: Ref<number> = ref(0)
    const importType: Ref<string> = ref('scheduled')
    const active: Ref<boolean> = ref(true)
    const defaultWeight: Ref<number> = ref(500)
    const defaultLength: Ref<number> = ref(34)
    const defaultWidth: Ref<number> = ref(24)
    const defaultHeight: Ref<number> = ref(7)
    const isButtonDisabled: Ref<boolean> = ref(false)
    const triggerOrderStatus: Ref<string> = ref('processing')

    const modalTitle = 'WooCommerce keys configuration'
    const modalName = WOO_COMMERCE_KEYS
    const importUrl: string = import.meta.env.VITE_APP_INTEGRATION_URL || ''

    const modalData: ComputedRef<WooCommerceIntegrationModal | null> = computed(
      () => modalStore.modalPropsByName(modalName) || null,
    )
    const customerId: ComputedRef<string> = computed(() => modalData.value?.customerId || '')
    const webhooksUrl: ComputedRef<string> = computed(
      () => `${importUrl}/webhook/woocommerce?client_internal_id=${customerId.value}`,
    )

    const connect = () => {
      if (isButtonDisabled.value) return
      isButtonDisabled.value = true
      customersIntegrationsStore
        .connect({
          customerId: customerId.value,
          data: {
            marketplace: WOOCOMMERCE,
            import_interval: importInterval.value,
            import_type: importType.value,
            active: active.value,
            default_weight: defaultWeight.value,
            default_length: defaultLength.value,
            default_width: defaultWidth.value,
            default_height: defaultHeight.value,
            credentials: {
              url: url.value,
              key: key.value,
              secret: secret.value,
            },
            configuration: {
              triggerOrderStatus: triggerOrderStatus.value,
            },
          },
        })
        .then((response) => {
          if (response.message) {
            notify({ group: 'error', text: response.message })
            isButtonDisabled.value = false
          } else {
            notify({ group: 'success', text: 'The integration has been connected.' })
            customersIntegrationsStore.fetch(toRaw(customerId.value))
            modalStore.closeModal(modalName)
          }
        })
        .catch(() => {
          isButtonDisabled.value = false
        })
    }

    watch(modalData, () => {
      if (!modalData.value) return
      url.value = modalData.value.url
      key.value = modalData.value.key
      secret.value = modalData.value.secret
      importInterval.value = modalData.value.importInterval
      importType.value = modalData.value.importType
      active.value = modalData.value.active
      defaultWeight.value = modalData.value.defaultWeight
      defaultLength.value = modalData.value.defaultLength
      defaultWidth.value = modalData.value.defaultWidth
      defaultHeight.value = modalData.value.defaultHeight
      triggerOrderStatus.value = modalData.value.triggerOrderStatus || 'processing'
    })

    return {
      modalData,
      url,
      key,
      secret,
      importInterval,
      importType,
      active,
      defaultWeight,
      defaultLength,
      defaultWidth,
      defaultHeight,
      triggerOrderStatus,
      isButtonDisabled,
      modalName,
      modalTitle,
      webhooksUrl,
      numbersOnly,
      connect,
    }
  },
})
</script>
